import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useNavigationStackSafe } from "@/contexts/NavigationStackContext";
import { getTabIdFromPath, getTabReselectAction, TAB_ID_TO_PATH, type TabId } from "@/routes/routeObjects";

// Re-export TabId for backward compatibility
export type { TabId } from "@/routes/routeObjects";

export const useTabNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navStack = useNavigationStackSafe();

  const currentTabId = getTabIdFromPath(location.pathname);

  const navigateToTab = useCallback(
    (tabId: TabId) => {
      if (!navStack) {
        navigate(TAB_ID_TO_PATH[tabId]);
        return;
      }
      if (currentTabId === tabId) {
        if (getTabReselectAction(tabId)) {
          navStack.emitReselect(tabId);
        }
        return;
      }
      // Reset map state when navigating away from the map tab
      if (currentTabId === "map") {
        navStack.setMapView(null);
        const mapPath = TAB_ID_TO_PATH.map;
        navStack.setTabLocation("map", { id: mapPath, pathname: mapPath, search: "", hash: "", state: null });
      }
      const tabLocation = navStack.getTabLocation(tabId);
      // Just navigate - StackedRouter handles state based on URL
      navigate(
        {
          pathname: tabLocation.pathname,
          search: tabLocation.search,
          hash: tabLocation.hash,
        },
        { state: tabLocation.state ?? null },
      );
    },
    [currentTabId, navStack, navigate],
  );

  const handleTabClick = useCallback(
    (tabId: TabId, event?: React.MouseEvent<HTMLElement>) => {
      if (!event) {
        navigateToTab(tabId);
        return;
      }

      if (event.metaKey || event.ctrlKey) {
        event.preventDefault();
        const path = navStack?.getTabPath(tabId) ?? TAB_ID_TO_PATH[tabId];
        const url = `${window.location.origin}${path}`;
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }

      if (event.button === 1 || event.button === 2) {
        return;
      }

      event.preventDefault();
      navigateToTab(tabId);
    },
    [navStack, navigateToTab],
  );

  const getTabIdFromPathForPath = useCallback((pathname: string) => getTabIdFromPath(pathname), []);

  return { currentTabId, navigateToTab, handleTabClick, getTabIdFromPath: getTabIdFromPathForPath };
};

export const useTabReselect = (tabId: TabId, handler: () => void) => {
  // Use safe version to handle edge cases during navigation/HMR where context may be temporarily unavailable
  const context = useNavigationStackSafe();
  // Extract stable callback to avoid re-running effect on every context change
  const registerReselect = context?.registerReselect;

  useEffect(() => {
    if (!registerReselect) return;
    return registerReselect(tabId, handler);
  }, [registerReselect, handler, tabId]);
};
